/* eslint-disable react/prop-types */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import api from "../../api/axios";
import EventMetaIcon from "./EventMetaIcon";
import { openExternalUrl } from "../../native/runtime";
import { getEventSkillLevelPresentation } from "./eventSkillLevelMeta";
import { isConceptAwaitingDetails } from "./conceptPresentation";

// Normalize links like "t.me/..." or "tg://..." to something window.open can handle.
const normalizeUrl = (url) => {
  const s = String(url || "").trim();
  if (!s) return null;

  // Already has protocol
  if (/^https?:\/\//i.test(s) || /^tg:\/\//i.test(s)) return s;

  // Common Telegram hosts without protocol
  if (/^(t\.me|telegram\.me|telegram\.dog)\//i.test(s)) return `https://${s}`;

  return s;
};

const EVENT_LANGUAGE_LABELS = {
  en: "English",
  sk: "Slovak",
  de: "German",
  es: "Spanish",
  fr: "French",
  it: "Italian",
  cs: "Czech",
  pl: "Polish",
  hu: "Hungarian",
  ru: "Russian",
  uk: "Ukrainian",
};

const EventMetaSection = ({
  ev,
  isParticipant,
  isHost,
  linkCopied,
  shareEvent,

  // Backward compatible UI controls
  variant = "desktop", // "desktop" | "mobile"
  showMeta = true, // show left-side meta icons (online/offline etc.)
}) => {
  const { t } = useTranslation("events");
  const navigate = useNavigate();
  const [openingChat, setOpeningChat] = useState(false);

  // Telegram invite link (legacy / external chat)
  const telegramChatLink =
    normalizeUrl(ev?.chat) ||
    normalizeUrl(ev?.telegram_chat_link) ||
    normalizeUrl(ev?.telegram_link) ||
    null;

  const canShowChatButton = Boolean((isParticipant || isHost) && ev?.id);

  const openEventChat = async (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    if (!ev?.id) return;

    // tiny UX guard to avoid double taps
    if (openingChat) return;

    // 1) If event has Telegram link -> open it
    if (telegramChatLink) {
      setOpeningChat(true);
      try {
        await openExternalUrl(telegramChatLink);
      } finally {
        setTimeout(() => setOpeningChat(false), 400);
      }
      return;
    }

    // 2) Otherwise -> internal platform chat
    try {
      setOpeningChat(true);

      // join (creates membership + system msg)
      const res = await api.post(`/chats/${ev.id}/internal-chat/join/`);
      const chatId = res?.data?.chat_id ?? null;

      const url = chatId
        ? `/menu/chats/event/${ev.id}?chat_id=${encodeURIComponent(chatId)}`
        : `/menu/chats/event/${ev.id}`;

      navigate(url);
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Failed to open chat";

      window.dispatchEvent(
        new CustomEvent("global-error", {
          detail: { message: msg, status },
        })
      );
    } finally {
      setOpeningChat(false);
    }
  };

  const isOnline = ev?.event_format === 0;
  const isPersonalHost = ev?.host_format === 0;
  const hostFormatLabel = isPersonalHost
    ? t("events.details.personal_host")
    : ev?.club?.name || ev?.business_name || t("events.details.business_host");
  const targetGender = String(ev?.target_gender || "").toLowerCase();
  const targetGenderLabel =
    targetGender === "female"
      ? t("events.details.target_gender_female")
      : targetGender === "male"
        ? t("events.details.target_gender_male")
        : "";
  const targetGenderIcon =
    targetGender === "female"
      ? "/assets/events/female_event.svg"
      : targetGender === "male"
        ? "/assets/events/male_event.svg"
        : "";
  const ageMinRaw = ev?.target_age_min;
  const ageMaxRaw = ev?.target_age_max;
  const hasAgeMin = ageMinRaw !== null && ageMinRaw !== undefined && String(ageMinRaw) !== "";
  const hasAgeMax = ageMaxRaw !== null && ageMaxRaw !== undefined && String(ageMaxRaw) !== "";
  const ageMin = hasAgeMin ? Number(ageMinRaw) : null;
  const ageMax = hasAgeMax ? Number(ageMaxRaw) : null;
  const eventLanguageCode = String(ev?.event_language || "").toLowerCase();
  const eventLanguageLabel =
    String(ev?.event_language_label || "").trim() ||
    EVENT_LANGUAGE_LABELS[eventLanguageCode] ||
    "";
  const requiredSkillLevel = getEventSkillLevelPresentation(ev?.required_skill_level, t);
  const minVaultLevel = Number(ev?.min_vault_level) || 0;
  const awaitingConceptDetails = isConceptAwaitingDetails(ev);
  let targetAgeLabel = "";
  if (Number.isFinite(ageMin) && Number.isFinite(ageMax)) {
    targetAgeLabel = `${ageMin}-${ageMax}`;
  } else if (Number.isFinite(ageMin)) {
    targetAgeLabel = `${ageMin}+`;
  } else if (Number.isFinite(ageMax)) {
    targetAgeLabel = `≤${ageMax}`;
  }

  const isMobile = variant === "mobile";

  return (
    <div
      className={
        isMobile
          ? "px-5 mb-2 text-white text-sm"
          : "px-5 mb-4 flex items-start justify-between gap-3 text-white text-sm"
      }
    >
      {showMeta && (
        <div
          className={
            isMobile
              ? "flex flex-wrap items-center gap-2 mb-2"
              : "flex min-w-0 flex-1 flex-wrap items-center gap-2"
          }
        >
          {/* Event format */}
          {!awaitingConceptDetails && <div className="flex items-center gap-1">
            <EventMetaIcon
              src={`/assets/events/${isOnline ? "online_icon.svg" : "offline_icon.svg"}`}
              alt={isOnline ? t("events.details.online") : t("events.details.offline")}
              className="w-5 h-5"
              imageClassName="brightness-200"
            />
            <span>{isOnline ? t("events.details.online") : t("events.details.offline")}</span>
          </div>}

          {/* Host format */}
          <div className="flex items-center gap-1">
            <EventMetaIcon
              src={`/assets/events/${isPersonalHost ? "personal_icon.svg" : "organization_icon.svg"}`}
              alt={
                isPersonalHost
                  ? t("events.details.personal_host")
                  : ev?.club?.name || t("events.details.business_host")
              }
              className="w-5 h-5"
              imageClassName="brightness-200"
            />
            <span>{hostFormatLabel}</span>
          </div>

          {/* Duration */}
          {ev?.approximate_duration && (
            <div className="flex items-center gap-1">
              <EventMetaIcon
                src="/assets/events/clock_icon.svg"
                alt={t("events.meta.duration_alt")}
                className="w-5 h-5"
                imageClassName="brightness-200"
              />
              <span>{t("events.meta.duration_label", { minutes: ev.approximate_duration })}</span>
            </div>
          )}

          {ev?.is_verified && (
            <div className="flex items-center gap-1">
              <EventMetaIcon
                src="/assets/events/verified_icon.svg"
                alt={t("events.details.official_event")}
                className="w-5 h-5"
                imageClassName="brightness-200"
              />
              <span>{t("events.details.official_event")}</span>
            </div>
          )}

          {ev?.join_by_request && (
            <div className="flex items-center gap-1">
              <EventMetaIcon
                src="/assets/events/private_event.svg"
                alt={t("events.details.private_requests_only")}
                className="w-5 h-5"
                imageClassName="brightness-200"
              />
              <span>{t("events.details.private_requests_only")}</span>
            </div>
          )}

          {requiredSkillLevel && (
            <div className="flex items-center gap-1">
              <EventMetaIcon
                src={requiredSkillLevel.iconSrc}
                alt={requiredSkillLevel.levelLabel}
                className="h-5 w-5"
              />
              <span>{requiredSkillLevel.label}</span>
            </div>
          )}

          {eventLanguageLabel && (
            <div className="flex items-center gap-1">
              <EventMetaIcon
                src="/assets/events/language_icon.svg"
                alt={t("events.details.event_language")}
                className="w-5 h-5"
                imageClassName="brightness-200"
              />
              <span>{eventLanguageLabel}</span>
            </div>
          )}

          {targetGenderLabel && targetGenderIcon && (
            <div className="flex items-center gap-1">
              <EventMetaIcon
                src={targetGenderIcon}
                alt={targetGenderLabel}
                className="w-5 h-5"
                imageClassName="brightness-200"
              />
              <span>{targetGenderLabel}</span>
            </div>
          )}

          {targetAgeLabel && (
            <div className="flex items-center gap-1">
              <EventMetaIcon
                src="/assets/events/age_icon.svg"
                alt="Age"
                className="w-5 h-5"
                imageClassName="brightness-200"
              />
              <span>
                {t("events.details.target_age_prefix")}: {targetAgeLabel}
              </span>
            </div>
          )}

          {awaitingConceptDetails && !targetAgeLabel && (
            <div className="flex items-center gap-1">
              <EventMetaIcon src="/assets/events/age_icon.svg" alt="" className="w-5 h-5" imageClassName="brightness-200" />
              <span>{t("discovery_filters.any_age", { defaultValue: "Any age" })}</span>
            </div>
          )}

          {minVaultLevel > (awaitingConceptDetails ? 1 : 0) && (
            <div className="flex items-center gap-1">
              <EventMetaIcon
                src="/assets/events/logo-go.svg"
                alt=""
                className="w-5 h-5"
              />
              <span>{t("events.details.min_vault_level", { level: minVaultLevel })}</span>
            </div>
          )}
        </div>
      )}

      {/* Actions: Chat + Share */}
      <div className={isMobile ? "flex flex-col gap-2" : "flex shrink-0 items-center gap-4"}>
        {canShowChatButton && (
          <button
            type="button"
            onClick={openEventChat}
            disabled={openingChat}
            className={`flex gap-1.5 items-center underline ${isMobile ? "" : "whitespace-nowrap"} ${openingChat ? "opacity-70 cursor-not-allowed" : ""}`}
          >
            <img
              src="/assets/events/chat_icon.svg"
              alt={t("events.meta.chat_alt")}
              className="w-4.5 h-4.5"
            />
            {openingChat
              ? t("events.details.opening_event_chat", { defaultValue: "Opening chat..." })
              : t("events.details.event_chat")}
          </button>
        )}

        <button
          type="button"
          onClick={shareEvent}
          className={`flex gap-1.5 items-center underline ${isMobile ? "" : "whitespace-nowrap"}`}
        >
          <img
            src="/assets/events/share_icon.svg"
            alt={t("events.meta.share_alt")}
            className="w-4.5 h-4.5"
          />
          {linkCopied ? t("events.details.link_copied") : t("events.details.share_with_friends")}
        </button>
      </div>
    </div>
  );
};

export default EventMetaSection;
